import React, { useState, useEffect, useRef } from 'react';
import { FiSearch, FiX, FiSettings } from 'react-icons/fi';
import { getApiEndpoint } from '../utils/api';
import './SearchBar.css';

const FIELD_OPTIONS = [
  { value: 'title', label: 'Title' },
  { value: 'title_and_abstract', label: 'Title & abstract' },
  { value: 'fulltext', label: 'Title, abstract & fulltext' },
];

const MODE_OPTIONS = [
  { value: 'boolean', label: 'Boolean' },
  { value: 'semantic', label: 'Semantic', badge: 'beta' },
];

const DEFAULT_OPTIONS = { field: 'title_and_abstract', mode: 'boolean' };

const SearchBar = ({ onSearch, initialQuery = '', loading = false, searchOptions = DEFAULT_OPTIONS, onSearchOptionsChange }) => {
  const [query, setQuery] = useState(initialQuery);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showOptionsDropdown, setShowOptionsDropdown] = useState(false);
  const searchRef = useRef(null);
  const optionsRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    setQuery(initialQuery);
  }, [initialQuery]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
      if (optionsRef.current && !optionsRef.current.contains(event.target)) {
        setShowOptionsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (value.length >= 2) {
      timeoutRef.current = setTimeout(async () => {
        try {
          const response = await fetch(getApiEndpoint(`/api/autocomplete?q=${encodeURIComponent(value)}`));
          const data = await response.json();
          setSuggestions(data.results || []);
          setShowSuggestions(true);
        } catch (error) {
          console.error('Error fetching suggestions:', error);
        }
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      onSearch(query.trim());
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion.title);
    onSearch(suggestion.title);
    setShowSuggestions(false);
  };

  const handleClear = () => {
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleOptionSelect = (key, value) => {
    const newOptions = { ...searchOptions, [key]: value };
    if (onSearchOptionsChange) {
      onSearchOptionsChange(newOptions);
    }
  };

  const isNonDefault =
    searchOptions.field !== DEFAULT_OPTIONS.field ||
    searchOptions.mode !== DEFAULT_OPTIONS.mode;

  return (
    <div className="search-bar-container" ref={searchRef}>
      <form onSubmit={handleSubmit} className="search-form">
        <div className="search-input-wrapper">
          <FiSearch className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Search for works, authors, institutions..."
            value={query}
            onChange={handleInputChange}
            disabled={loading}
          />
          {query && (
            <button
              type="button"
              className="clear-button"
              onClick={handleClear}
              aria-label="Clear search"
            >
              <FiX />
            </button>
          )}
          <div className="search-options-anchor" ref={optionsRef}>
            <button
              type="button"
              className={`search-options-btn${isNonDefault ? ' active' : ''}`}
              onClick={() => setShowOptionsDropdown(prev => !prev)}
              aria-label="Search options"
              title="Search options"
            >
              <FiSettings />
              {isNonDefault && <span className="search-options-dot" />}
            </button>
            {showOptionsDropdown && (
              <div className="search-options-dropdown">
                <div className="search-options-section-label">Search fields</div>
                {FIELD_OPTIONS.map(opt => (
                  <div
                    key={opt.value}
                    className={`search-option-item${searchOptions.field === opt.value ? ' selected' : ''}`}
                    onClick={() => handleOptionSelect('field', opt.value)}
                  >
                    <span className={`radio-circle${searchOptions.field === opt.value ? ' checked' : ''}`} />
                    <span>{opt.label}</span>
                  </div>
                ))}
                <div className="search-options-divider" />
                <div className="search-options-section-label">Mode</div>
                {MODE_OPTIONS.map(opt => (
                  <div
                    key={opt.value}
                    className={`search-option-item${searchOptions.mode === opt.value ? ' selected' : ''}`}
                    onClick={() => handleOptionSelect('mode', opt.value)}
                  >
                    <span className={`radio-circle${searchOptions.mode === opt.value ? ' checked' : ''}`} />
                    <span>{opt.label}</span>
                    {opt.badge && <span className="beta-badge">{opt.badge}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button
            type="submit"
            className="search-button"
            disabled={loading || !query.trim()}
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </form>

      {showSuggestions && suggestions.length > 0 && (
        <div className="suggestions-dropdown">
          {suggestions.map((suggestion, index) => (
            <div
              key={index}
              className="suggestion-item"
              onClick={() => handleSuggestionClick(suggestion)}
            >
              <FiSearch className="suggestion-icon" />
              <span>{suggestion.title}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
